// ProcessTimeline.js
import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

const ProcessTimeline = () => {
  const timelines = [
    {
      pathway: 'Express Entry',
      stages: [
        { step: 'Create Express Entry Profile', duration: '1-2 weeks', description: 'Complete online profile with all required information and documents' },
        { step: 'Receive Invitation to Apply (ITA)', duration: 'Varies', description: 'Wait for ITA based on CRS score and draw frequency (typically 2-4 weeks between draws)' },
        { step: 'Submit Complete Application', duration: '60 days', description: 'Gather and submit all required documents within 60 days of receiving ITA' },
        { step: 'Application Processing', duration: '6 months', description: 'IRCC processes application including background checks and verification' },
        { step: 'Receive Confirmation of PR', duration: '1-2 weeks', description: 'Receive Confirmation of Permanent Residence (COPR) and PR visa' }
      ],
      totalTime: '6-8 months',
      color: 'from-blue-500 to-blue-600'
    },
    {
      pathway: 'Provincial Nominee Program',
      stages: [
        { step: 'Apply to Provincial Program', duration: '2-4 months', description: 'Submit application to specific provincial stream' },
        { step: 'Receive Provincial Nomination', duration: 'Varies by province', description: 'Province reviews and issues nomination certificate' },
        { step: 'Apply for PR (Paper or Express Entry)', duration: '1-2 weeks', description: 'Submit federal PR application with nomination certificate' },
        { step: 'Federal Processing', duration: '15-19 months', description: 'IRCC processes federal PR application' },
        { step: 'Receive Confirmation of PR', duration: '1-2 weeks', description: 'Receive COPR and PR visa' }
      ],
      totalTime: '18-24 months',
      color: 'from-green-500 to-green-600'
    },
    {
      pathway: 'Family Sponsorship (Spouse)',
      stages: [
        { step: 'Sponsor Eligibility Assessment', duration: '1-2 weeks', description: 'Confirm sponsor meets requirements' },
        { step: 'Prepare and Submit Application', duration: '4-6 weeks', description: 'Gather documents and submit complete sponsorship package' },
        { step: 'Application in Process', duration: '12 months', description: 'IRCC processes sponsorship and PR application together' },
        { step: 'Interview (if required)', duration: 'Varies', description: 'Attend interview if requested by IRCC' },
        { step: 'Receive Confirmation of PR', duration: '2-4 weeks', description: 'Receive COPR and PR visa' }
      ],
      totalTime: '12-14 months',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Standard Processing Timelines
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Understanding the typical timeline for each pathway helps you plan your immigration journey effectively.
          </p>
        </motion.div>

        {/* Timelines */}
        <div className="space-y-12">
          {timelines.map((timeline, tIndex) => (
            <motion.div
              key={tIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: tIndex * 0.2 }}
              className="bg-white rounded-lg shadow-sm p-6 md:p-8"
            >
              {/* Timeline Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-[#202356] mb-2">
                    {timeline.pathway}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock size={16} />
                    <span className="font-light">Total Processing Time: <strong>{timeline.totalTime}</strong></span>
                  </div>
                </div>
              </div>

              {/* Timeline Steps */}
              <div className="relative">
                {/* Vertical Line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                {/* Steps */}
                <div className="space-y-8">
                  {timeline.stages.map((stage, sIndex) => (
                    <motion.div
                      key={sIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: sIndex * 0.1 }}
                      className="relative pl-12"
                    >
                      {/* Step Number */}
                      <div className={`absolute left-0 w-8 h-8 rounded-full bg-gradient-to-br ${timeline.color} flex items-center justify-center text-white text-sm font-semibold`}>
                        {sIndex + 1}
                      </div>

                      {/* Step Content */}
                      <div className="bg-[#F5F8FA] p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-base font-semibold text-[#202356]">
                            {stage.step}
                          </h4>
                          <span className="text-xs font-light text-[#2542D4] bg-white px-3 py-1 rounded-full whitespace-nowrap ml-4">
                            {stage.duration}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm font-light leading-relaxed">
                          {stage.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Important Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-[#202356] mb-3">
              Processing Time Factors
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Completeness of application and supporting documents</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Background check complexity and country of origin</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Current IRCC workload and processing capacity</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Need for additional verification or interviews</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-[#202356] mb-3">
              How We Help
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Ensure application completeness to avoid delays</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Prepare all documents according to IRCC standards</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Monitor application status and respond to requests promptly</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Provide guidance throughout the entire process</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
